/**
 * Talk-down control. Self-contained so it can be dropped next to any camera:
 * it gates itself (flag + permission + site type) and renders nothing when
 * the operator cannot use it.
 *
 * Two transports behind one look:
 * - "clip" (v1, ENG-2135): hold to record up to 30 s, release to send, then
 *   a plain-words outcome. Audio input is useAudioRecorder, transport is
 *   useTalkDown.
 * - "live" (ENG-1721, behind `showLiveTalkDown`): hold to talk live over
 *   WHIP; transport is useLiveTalkDown, which never uses the recorder.
 * Without `showLiveTalkDown` the control is exactly v1.
 */
import React from "react";
import MicIcon from "@mui/icons-material/Mic";
import { useAudioRecorder } from "./useAudioRecorder";
import { useTalkDown } from "./useTalkDown";
import {
  TalkDownGate,
  TalkDownMode,
  useTalkDownEnabled,
  useTalkDownMode,
} from "./useTalkDownEnabled";
import {
  TALK_DOWN_MAX_SECONDS,
  TALK_DOWN_NEEDS_PRODUCTION_MODE_MESSAGE,
  TALK_DOWN_NEEDS_PRODUCTION_MODE_STAFF_MESSAGE,
  TalkDownPollOptions,
} from "./talkDownApi";
import { liveMediaNeedsProductionMode } from "../../utils/productionMode";
import { useIsStaffUser } from "../../utils/staffUser";
import {
  formatLiveElapsed,
  LiveStatusPollOptions,
  useLiveTalkDown,
} from "./useLiveTalkDown";
import { WhipDeps } from "./whipPublish";
import {
  TALK_DOWN_NO_SPEAKER_NOTE,
  useTalkDownLearnedIncapable,
} from "./talkDownCapabilityStore";

export const TALK_DOWN_BUTTON_LABEL = "Hold to talk down";
export const TALK_DOWN_HOLD_HINT = `Release to send (up to ${TALK_DOWN_MAX_SECONDS} s)`;

/** Test hooks for the live transport. */
export interface LiveTalkDownTestOptions {
  statusPoll?: LiveStatusPollOptions;
  idleMs?: number;
  whipDeps?: WhipDeps;
}

export interface TalkDownButtonProps extends TalkDownGate {
  cameraId: string | number | null | undefined;
  cameraName: string;
  /** "inline" sits in an action row; "overlay" sits on a dark video frame. */
  variant?: "inline" | "overlay";
  /**
   * Transport. Omitted = live when `showLiveTalkDown` is on, else clip.
   * "clip" pins v1; "live" without the flag still falls back to clip.
   */
  mode?: TalkDownMode;
  /** Test hook: shorter poll interval/timeout. */
  pollOptions?: TalkDownPollOptions;
  /** Test hook: live transport timings and WebRTC/fetch injection. */
  liveOptions?: LiveTalkDownTestOptions;
}

type StatusTone = "error" | "ok" | "neutral";

interface ControlViewProps {
  variant: "inline" | "overlay";
  label: string;
  status: string;
  statusTone: StatusTone;
  /** Technical cause, shown under the status for staff only. */
  detail?: string;
  /** The button is held and doing its thing (recording / live). */
  active: boolean;
  /** Connecting: held, but not live yet. */
  pending?: boolean;
  disabled: boolean;
  title: string;
  onPress: () => void;
  onRelease: () => void;
}

const isActivationKey = (key: string) => key === " " || key === "Enter";

const TalkDownControlView = ({
  variant,
  label,
  status,
  statusTone,
  detail,
  active,
  pending = false,
  disabled,
  title,
  onPress,
  onRelease,
}: ControlViewProps) => {
  const heldKeyRef = React.useRef(false);
  const staff = useIsStaffUser();
  const showDetail = Boolean(staff && detail && status);
  const overlay = variant === "overlay";
  const buttonTone = active
    ? "bg-[#D93636] text-white"
    : disabled || pending
    ? "bg-[#9CA3AF] text-white"
    : "bg-actuate-orange text-white hover:bg-[#e67700]";
  const statusColor =
    statusTone === "error"
      ? overlay
        ? "text-[#FFB4B4]"
        : "text-[#B3261E]"
      : statusTone === "ok"
      ? overlay
        ? "text-[#A7F3D0]"
        : "text-[#1B7F3B]"
      : overlay
      ? "text-white/90"
      : "text-actuate-blue";
  const holding = active || pending;

  return (
    <div
      className={`flex items-center gap-2 font-mulish ${
        overlay ? "rounded bg-black/60 px-2 py-1.5" : ""
      }`}
      data-testid="talk-down"
    >
      {status && (
        <span className="flex max-w-[260px] flex-col">
          <span
            role="status"
            className={`text-[12px] leading-tight ${statusColor}`}
          >
            {status}
          </span>
          {showDetail && (
            <span
              data-testid="talk-down-detail"
              className={`mt-0.5 break-all font-mono text-[10px] leading-tight ${
                overlay ? "text-white/50" : "text-gray-500"
              }`}
            >
              {detail}
            </span>
          )}
        </span>
      )}
      <button
        type="button"
        aria-label={TALK_DOWN_BUTTON_LABEL}
        aria-pressed={active}
        disabled={disabled}
        title={title}
        onPointerDown={(event) => {
          if (event.button !== undefined && event.button !== 0) return;
          event.preventDefault();
          onPress();
        }}
        onPointerUp={onRelease}
        onPointerLeave={() => {
          if (holding) onRelease();
        }}
        onPointerCancel={onRelease}
        onKeyDown={(event) => {
          if (!isActivationKey(event.key) || event.repeat) return;
          event.preventDefault();
          heldKeyRef.current = true;
          onPress();
        }}
        onKeyUp={(event) => {
          if (!isActivationKey(event.key) || !heldKeyRef.current) return;
          heldKeyRef.current = false;
          onRelease();
        }}
        onBlur={() => {
          if (holding) onRelease();
        }}
        onContextMenu={(event) => event.preventDefault()}
        style={{ touchAction: "none", userSelect: "none" }}
        className={`inline-flex h-9 select-none items-center gap-1 whitespace-nowrap rounded px-3 text-[13px] font-bold disabled:cursor-default ${buttonTone}`}
      >
        <MicIcon sx={{ fontSize: 18 }} />
        {label}
      </button>
    </div>
  );
};

interface TransportProps {
  cameraId: string | number;
  cameraName: string;
  variant: "inline" | "overlay";
  pollOptions?: TalkDownPollOptions;
}

/** v1: record a clip, send it, report the outcome. */
const ClipTalkDown = ({
  cameraId,
  cameraName,
  variant,
  pollOptions,
}: TransportProps) => {
  const talkDown = useTalkDown({ cameraId, cameraName, poll: pollOptions });
  const recorder = useAudioRecorder({
    maxSeconds: TALK_DOWN_MAX_SECONDS,
    onRecorded: talkDown.submit,
  });

  const { recording, elapsedSeconds } = recorder;
  const { busy, state } = talkDown;

  const label = recording
    ? `Recording ${elapsedSeconds}s`
    : state.phase === "sending"
    ? "Sending..."
    : state.phase === "waiting"
    ? "Waiting..."
    : "Hold to talk";

  const status = recording
    ? TALK_DOWN_HOLD_HINT
    : recorder.error ?? (state.phase === "idle" ? "" : state.message);
  const statusTone: StatusTone =
    recorder.error || state.phase === "error" || state.phase === "failed"
      ? "error"
      : state.phase === "played"
      ? "ok"
      : "neutral";

  return (
    <TalkDownControlView
      variant={variant}
      label={label}
      status={status}
      statusTone={statusTone}
      detail={recording || recorder.error ? undefined : state.detail}
      active={recording}
      disabled={busy}
      title="Hold to record, release to send"
      onPress={() => {
        if (busy) return;
        recorder.clearError();
        recorder.start();
      }}
      onRelease={() => recorder.stop()}
    />
  );
};

/** ENG-1721: hold to talk live; the session stays open between holds. */
const LiveTalkDown = ({
  cameraId,
  cameraName,
  variant,
  liveOptions,
}: Omit<TransportProps, "pollOptions"> & {
  liveOptions?: LiveTalkDownTestOptions;
}) => {
  const live = useLiveTalkDown({
    cameraId,
    cameraName,
    statusPoll: liveOptions?.statusPoll,
    idleMs: liveOptions?.idleMs,
    whipDeps: liveOptions?.whipDeps,
  });
  const { state, elapsedSeconds, busy } = live;

  const label =
    state.phase === "live"
      ? `Live — ${formatLiveElapsed(elapsedSeconds)}`
      : state.phase === "connecting"
      ? "Connecting..."
      : state.phase === "waiting"
      ? "Ending..."
      : state.phase === "closing"
      ? "Closing..."
      : "Hold to talk";
  const status = state.phase === "idle" ? "" : state.message;
  const statusTone: StatusTone =
    state.phase === "error" || state.phase === "failed"
      ? "error"
      : state.phase === "ended"
      ? "ok"
      : "neutral";

  return (
    <TalkDownControlView
      variant={variant}
      label={label}
      status={status}
      statusTone={statusTone}
      detail={state.detail}
      active={state.phase === "live"}
      pending={state.phase === "connecting"}
      disabled={busy}
      title="Hold to talk live, release to stop"
      onPress={live.press}
      onRelease={live.release}
    />
  );
};

export const TalkDownButton = ({
  cameraId,
  cameraName,
  integrationTypeName,
  skipSiteTypeGate,
  talkDownCapable,
  variant = "inline",
  mode,
  pollOptions,
  liveOptions,
}: TalkDownButtonProps) => {
  const enabled = useTalkDownEnabled({
    integrationTypeName,
    skipSiteTypeGate,
    talkDownCapable,
  });
  const effectiveMode = useTalkDownMode(mode);
  const learnedIncapable = useTalkDownLearnedIncapable(cameraId);
  const staff = useIsStaffUser();

  if (!enabled || cameraId == null || cameraId === "") return null;

  // dev/redesign on the staging API with Use production off: there are no
  // real cameras to talk to, so a start can only fail. Show the control
  // disabled with the reason and never mount a transport (no mic prompt, no
  // POST). Prod-config builds never take this path.
  if (liveMediaNeedsProductionMode()) {
    return (
      <TalkDownControlView
        variant={variant}
        label="Hold to talk"
        status={
          staff
            ? TALK_DOWN_NEEDS_PRODUCTION_MODE_STAFF_MESSAGE
            : TALK_DOWN_NEEDS_PRODUCTION_MODE_MESSAGE
        }
        statusTone="neutral"
        detail="staging API · Use production is off"
        active={false}
        disabled
        title="Talk-down needs real cameras"
        onPress={() => undefined}
        onRelease={() => undefined}
      />
    );
  }

  // ENG-1721: a talk-down on this camera came back no_backchannel /
  // unsupported_camera this page session. Replace the button with a note
  // until reload (not persisted; the backend flag is the durable answer).
  if (learnedIncapable) {
    return (
      <div
        className={`flex items-center font-mulish ${
          variant === "overlay" ? "rounded bg-black/60 px-2 py-1.5" : ""
        }`}
        data-testid="talk-down-no-speaker"
      >
        <span
          role="status"
          className={`text-[12px] leading-tight ${
            variant === "overlay" ? "text-white/90" : "text-actuate-blue"
          }`}
        >
          {TALK_DOWN_NO_SPEAKER_NOTE}
        </span>
      </div>
    );
  }

  return effectiveMode === "live" ? (
    <LiveTalkDown
      // A different camera is a different session: remount.
      key={`live-${cameraId}`}
      cameraId={cameraId}
      cameraName={cameraName}
      variant={variant}
      liveOptions={liveOptions}
    />
  ) : (
    <ClipTalkDown
      cameraId={cameraId}
      cameraName={cameraName}
      variant={variant}
      pollOptions={pollOptions}
    />
  );
};
